// @flow
import { updateForm } from "../_modularui/ModularUIActions";

import { getSetting } from "../../constants/Settings";

import { validateFormObject } from "./FormValidations";
import { autosaveFormObject } from "./FormAutosave";
import { autosubmitFormObject } from "./FormAutosubmit";
import { autoupdateFormObject } from "./FormAutoupdate";

import type { Dispatch, ThunkAction, UpdateFormOptions } from "../types";
import type { AttributeType } from "../../models/types";
import type FormModel from "../../models/form/FormModel";
import type FormObjectModel from "../../models/form/FormObjectModel";

/**
 * Update an attribute on a form
 */
export const updateFormAttribute =
  (
    form: FormModel,
    formObject: FormObjectModel,
    attribute: AttributeType,
    inputvalue: string,
    options: UpdateFormOptions = {
      autosubmit: false,
      autosave: false, // autosave commits the form in the background
      autoupdate: false, // autoupdate does not commit and updates the form with for example a new dynamicschema (work-around for form-tree iq)
      forceUpdate: false,
      validate: true,
    },
  ): ThunkAction<> =>
  (dispatch: Dispatch) => {
    const newForm = form.clone();

    if (options.validate === undefined) {
      options.validate = true;
    }

    if (newForm.currentFormObject?.equals(formObject)) {
      newForm.currentFormObject.updateAttribute(attribute, inputvalue);
    } else {
      newForm.completedFormObjects.forEach((completeObject) => {
        if (completeObject.equals(formObject)) {
          completeObject.updateAttribute(attribute, inputvalue);
        }
      });
    }

    if (options.autosubmit && newForm.isValid) {
      const autosubmitAction = autosubmitFormObject(
        newForm,
        attribute,
        options.forceUpdate,
      );
      if (autosubmitAction) {
        return dispatch(autosubmitAction);
      }
    }

    if (options.autoupdate && newForm.isValid) {
      const autoupdateAction = autoupdateFormObject(newForm, attribute);
      if (autoupdateAction) {
        return dispatch(autoupdateAction);
      }
    }

    handleAutoSave(dispatch, newForm, options);

    handleValidate(dispatch, newForm, options);

    return dispatch(updateForm(newForm));
  };

const handleAutoSave = (
  dispatch: Dispatch,
  newForm: FormModel,
  options: UpdateFormOptions,
) => {
  if (options.autosave && newForm.isValid && newForm.isChanged()) {
    dispatch(autosaveFormObject(newForm));
  }
};

const handleValidate = (
  dispatch: Dispatch,
  newForm: FormModel,
  options: UpdateFormOptions,
) => {
  if (
    options.validate &&
    newForm.currentFormObject?.hasDynamicValidations &&
    getSetting("USE_INSTANT_SERVER_VALIDATION")
  ) {
    // server form validations happens async, don't wait for the form to return
    dispatch(validateFormObject(newForm));
  }
};
